import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { employerAPI, getProxiedLogoUrl } from '../../services/api';
import toast from 'react-hot-toast';
import EditEmployerProfile from '../EditEmployerProfile';
import EmployerEmployees from '../EmployerEmployees';
import JobPosting from '../JobPosting';
import JobList from '../JobList';
import CandidateList from '../CandidateList';
import ApplicationList from '../ApplicationList';

const EmployerDashboard = ({ user }) => {
  const [employerView, setEmployerView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showJobPostingModal, setShowJobPostingModal] = useState(false);
  const [employerProfile, setEmployerProfile] = useState(null);
  const [showEmployerProfileModal, setShowEmployerProfileModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [employerKPIs, setEmployerKPIs] = useState({
    jobPostingsCount: 0,
    employeesCount: 0,
    candidatesCount: 0,
    applicationsCount: 0,
    isLoading: true
  });

  const apiBaseUrl = useMemo(
    () => (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, ''),
    []
  );

  const refreshEmployerProfile = useCallback(async () => {
    if (user?.userType !== 'employer' && user?.userType !== 'employer_staff') {
      return;
    }
    try {
      const response = await employerAPI.getProfile();
      setEmployerProfile(response.data);
    } catch (error) {
      console.error('Error fetching employer profile:', error);
      toast.error('Failed to load employer profile. Please try again later.');
    }
  }, [user]);

  useEffect(() => {
    refreshEmployerProfile();
  }, [refreshEmployerProfile]);

  // Fetch employer KPI metrics
  useEffect(() => {
    const fetchEmployerKPIs = async () => {
      if (user?.userType !== 'employer' && user?.userType !== 'employer_staff') {
        return;
      }
      try {
        setEmployerKPIs(prev => ({ ...prev, isLoading: true }));
        
        // Fetch all KPI data in parallel
        const [jobsResponse, employeesResponse, applicationsResponse, candidatesResponse] = await Promise.allSettled([
          employerAPI.getJobPostings(),
          employerAPI.getEmployees(),
          employerAPI.getApplications(1, 1),
          employerAPI.getAllCandidates(1, 1, '')
        ]);

        // Count job postings
        let jobPostingsCount = 0;
        if (jobsResponse.status === 'fulfilled') {
          const jobs = jobsResponse.value.data?.jobs || jobsResponse.value.data || [];
          jobPostingsCount = Array.isArray(jobs) ? jobs.length : 0;
        }

        // Count employees
        let employeesCount = 0;
        if (employeesResponse.status === 'fulfilled') {
          const employees = employeesResponse.value.data?.employees || employeesResponse.value.data || [];
          employeesCount = Array.isArray(employees) ? employees.length : 0;
        }

        // Count total applications
        let applicationsCount = 0;
        if (applicationsResponse.status === 'fulfilled') {
          const applicationsData = applicationsResponse.value.data;
          if (applicationsData?.total !== undefined) {
            applicationsCount = applicationsData.total;
          } else {
            const applications = applicationsData?.applications || applicationsData || [];
            applicationsCount = Array.isArray(applications) ? applications.length : 0;
          }
        }

        // Count total candidates
        let candidatesCount = 0;
        if (candidatesResponse.status === 'fulfilled') {
          const candidatesData = candidatesResponse.value.data;
          if (candidatesData?.total !== undefined) {
            candidatesCount = candidatesData.total;
          } else {
            const candidates = candidatesData?.candidates || candidatesData || [];
            candidatesCount = Array.isArray(candidates) ? candidates.length : 0;
          }
        }

        setEmployerKPIs({
          jobPostingsCount,
          employeesCount,
          candidatesCount,
          applicationsCount,
          isLoading: false
        });
      } catch (error) {
        console.error('Error fetching employer KPIs:', error);
        setEmployerKPIs(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchEmployerKPIs();
  }, [user]);

  const employerLogoUrl = useMemo(() => {
    const rawLogo =
      employerProfile?.companyLogoUrl ||
      user?.companyLogoUrl ||
      employerProfile?.company_logo_url ||
      user?.company_logo_url;
    return getProxiedLogoUrl(rawLogo, apiBaseUrl);
  }, [apiBaseUrl, employerProfile, user]);

  const employeeRole = useMemo(() => {
    if (!user) return null;
    if (user.employeeRole) return user.employeeRole;
    if (employerProfile?.currentUserRole) return employerProfile.currentUserRole;
    return null;
  }, [user, employerProfile?.currentUserRole]);

  const canManageEmployees = user?.userType === 'employer' || employeeRole === 'hr_manager';
  const canEditCompanyProfile = user?.userType === 'employer' || employeeRole === 'hr_manager';
  const canCreateJobPosting = user?.userType === 'employer' || employeeRole === 'hr_manager' || employeeRole === 'hr_recruiter';

  return (
    <>
      <div className="flex h-[calc(100vh-80px)] overflow-hidden relative">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <aside className={`
          ${sidebarOpen ? 'w-64' : 'w-0'} 
          bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden
          flex flex-col
          fixed lg:relative inset-y-0 left-0 z-40 lg:z-auto
        `}>
          <div className="flex-1 overflow-y-auto p-3 z-50 relative bg-white">
            {/* Navigation Items */}
            <nav className="space-y-0.5">
              {/* Dashboard (Overview) */}
              <button
                onClick={() => setEmployerView('dashboard')}
                className={`side-nav-item ${
                  employerView === 'dashboard' ? 'side-nav-item-active' : ''
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span>Dashboard</span>
              </button>

              {/* Jobs Posted */}
              <button
                onClick={() => setEmployerView('jobs')}
                className={`side-nav-item ${
                  employerView === 'jobs'
                    ? 'side-nav-item-active'
                    : ''
                  }
                `}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Jobs Posted</span>
              </button>

              {/* Team Members */}
              <button
                onClick={() => setEmployerView('team')}
                className={`side-nav-item ${
                  employerView === 'team'
                    ? 'side-nav-item-active'
                    : ''
                  }
                `}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>Team Members</span>
              </button>

              {/* Candidates */}
              <button
                onClick={() => setEmployerView('candidates')}
                className={`side-nav-item ${
                  employerView === 'candidates'
                    ? 'side-nav-item-active'
                    : ''
                  }
                `}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Candidates</span>
              </button>

              {/* Applications */}
              <button
                onClick={() => setEmployerView('applications')}
                className={`side-nav-item ${
                  employerView === 'applications'
                    ? 'side-nav-item-active'
                    : ''
                  }
                `}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Applications</span>
              </button>

              {/* Settings */}
              <button
                onClick={() => setEmployerView('settings')}
                className={`side-nav-item ${
                  employerView === 'settings'
                    ? 'side-nav-item-active'
                    : ''
                  }
                `}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Settings</span>
              </button>
            </nav>

            {/* Actions Section */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Actions</h3>
              <div className="space-y-1">
                {canCreateJobPosting && (
                  <button
                    onClick={() => setShowJobPostingModal(true)}
                    className="side-nav-item"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Create Job Posting</span>
                  </button>
                )}
                {canEditCompanyProfile && (
                  <button
                    onClick={() => setShowEmployerProfileModal(true)}
                    className="side-nav-item"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5h2m-1 0v14m-7-7h14" />
                    </svg>
                    <span>Edit Company Profile</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            {/* Header with toggle button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h2 className="text-xl font-semibold text-secondary-900">
                  {employerView === 'dashboard' && 'Dashboard'}
                  {employerView === 'jobs' && 'Jobs Posted'}
                  {employerView === 'team' && 'Team Members'}
                  {employerView === 'candidates' && 'Candidates'}
                  {employerView === 'applications' && 'Job Applications'}
                  {employerView === 'settings' && 'Settings'}
                </h2>
              </div>
            </div>

            {/* Dashboard Overview */}
            <div className="space-y-4">
              {employerView === 'dashboard' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="ui-card p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-secondary-600">Job Openings</h3>
                        <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0h-8m8 0v2a2 2 0 01-2 2H8a2 2 0 01-2-2V6" />
                        </svg>
                      </div>
                      <p className="text-3xl font-bold text-secondary-900">
                        {employerKPIs.isLoading ? '...' : employerKPIs.jobPostingsCount}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">No of Job Posted</p>
                    </div>
                    
                    <div className="ui-card p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-secondary-600">No of Employee</h3>
                        <svg className="w-8 h-8 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <p className="text-3xl font-bold text-secondary-900">
                        {employerKPIs.isLoading ? '...' : employerKPIs.employeesCount}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">Total Employees</p>
                    </div>
                    
                    <div className="ui-card p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-secondary-600">Candidates</h3>
                        <svg className="w-8 h-8 text-info-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                      <p className="text-3xl font-bold text-secondary-900">
                        {employerKPIs.isLoading ? '...' : employerKPIs.candidatesCount}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">No of candidates available</p>
                    </div>
                    
                    <div className="ui-card p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-secondary-600">Number of applications</h3>
                        <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-3xl font-bold text-secondary-900">
                        {employerKPIs.isLoading ? '...' : employerKPIs.applicationsCount}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">Total applications</p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="ui-card p-6">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {canCreateJobPosting && (
                        <button
                          onClick={() => setShowJobPostingModal(true)}
                          className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary-100 rounded-lg">
                              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Create Job Posting</p>
                              <p className="text-sm text-gray-500">Post a new job opening</p>
                            </div>
                          </div>
                        </button>
                      )}
                      <button
                        onClick={() => setEmployerView('candidates')}
                        className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">View Candidates</p>
                            <p className="text-sm text-gray-500">Browse matched candidates</p>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => setEmployerView('team')}
                        className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Manage Team</p>
                            <p className="text-sm text-gray-500">Add or manage team members</p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Jobs Posted */}
              {employerView === 'jobs' && (
                <JobList 
                  onJobUpdated={() => {}} 
                  onViewMatchedCandidates={(jobId) => {
                    setSelectedJobId(jobId);
                    setEmployerView('candidates');
                  }}
                />
              )}

              {/* Team Members */}
              {employerView === 'team' && (
                <EmployerEmployees canManage={canManageEmployees} />
              )}

              {/* Candidates */}
              {employerView === 'candidates' && (
                <CandidateList jobId={selectedJobId} onJobIdClear={() => setSelectedJobId(null)} />
              )}

              {/* Applications */}
              {employerView === 'applications' && (
                <ApplicationList />
              )}

              {/* Settings */}
              {employerView === 'settings' && (
                <div className="ui-card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Settings</h3>
                  <div className="space-y-4">
                    {canEditCompanyProfile && (
                      <button
                        onClick={() => setShowEmployerProfileModal(true)}
                        className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5h2m-1 0v14m-7-7h14" />
                          </svg>
                          <span className="font-medium text-gray-900">Company Profile</span>
                        </div>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-500">More settings coming soon...</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Job Posting Modal */}
      {showJobPostingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Create Job Posting</h3>
              <button
                onClick={() => setShowJobPostingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <JobPosting
                onSuccess={() => {
                  setShowJobPostingModal(false);
                  toast.success('Job posting created successfully!');
                }}
                onCancel={() => setShowJobPostingModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Employer Profile Modal */}
      {showEmployerProfileModal && (
        <EditEmployerProfile
          onClose={() => setShowEmployerProfileModal(false)}
          onSuccess={() => {
            refreshEmployerProfile();
          }}
        />
      )}
    </>
  );
};

export default EmployerDashboard;

